<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import type { ReleaseType, ReleasePayloadType } from "@/common/types/release";

import { baseInput, baseSelect, baseTextEditor } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "salve:data"]);

const props = defineProps<{
  data?: ReleaseType | null;
  isPatch?: boolean;
}>();

const DEFAULT_DATA = {
  name: "",
  status: "ACTIVE",
  description: "",
};

const payload = ref<ReleasePayloadType>({ ...DEFAULT_DATA });

const optionStatus = computed(() => [
  { value: "ACTIVE", option: t("common.labels.active") },
  { value: "INACTIVE", option: t("common.labels.inactive") },
]);

function update(key: keyof typeof payload.value) {
  if (props.isPatch) {
    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  const { name, status, description } = props.data;

  payload.value = {
    ...payload.value,
    name,
    status,
    description,
  };
}

const debouceUpdate = debounce(update, 300);

watchEffect(() => {
  resumeData();
});
</script>

<template>
  <section class="form-release">
    <base-input
      v-model="payload.name"
      :value="payload.name"
      :placeholder="t('release.placeholders.name')"
      required
      @update:modelValue="debouceUpdate('name')"
    >
      <template #label> {{ t("release.labels.name") }} * </template>
    </base-input>

    <base-select
      v-model="payload.status"
      :value="payload.status"
      :options="optionStatus"
      :is-empty="false"
      @change="debouceUpdate('status')"
    >
      <template #label> {{ t("common.labels.status") }} *</template>
    </base-select>

    <base-text-editor
      v-model="payload.description"
      @update:modelValue="debouceUpdate('description')"
    >
      <template #label> {{ t("release.labels.description") }}</template>
    </base-text-editor>
  </section>
</template>

<style lang="scss" scoped>
.form-release {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
